/**
 * Slot Check Search (internal name: Universal Search).
 *
 * Before any search: every casino the search covers is a pin on the world map
 * (GetUniversalSearchCasinos, Mongo `casinos` only; clustered when zoomed out,
 * map fit around them) and the drawer sits low (universalSearchDrawerEmptyPercent,
 * default 20%: handle, title, subtitle and prompt only).
 *
 * One search across every active SlotCheck casino. Layout like Explore:
 * Casino Selection header → world map (casinos with matches, centered on the
 * casino with the most matching EGMs) → results drawer that starts at the
 * configured height (default 50%) and slides up over the map. Results group
 * By Casino or By Manufacturer. Tapping an EGM or a game enters that casino the
 * normal way (ChooseCasinos onClickCasino) and Explore opens filtered to it.
 *
 * Access: users.slotCheckSearchEnabled = 1 / "1" only (lib/slotCheckSearchAccess).
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { IonContent, IonIcon, IonPage, IonSpinner, useIonViewWillLeave } from '@ionic/react';
import { InputAdornment, TextField } from '@material-ui/core';
import { closeCircle, search } from 'ionicons/icons';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Subscription } from 'rxjs';

import CasinoNavBar from 'components/CasinoNavBar';
import { IState } from 'appRedux/createStore';
import {
  CasinoService,
  IUniversalSearchCasino,
  IUniversalSearchCasinoPin,
  IUniversalSearchResponse,
  IUniversalSearchSuggestResponse,
  UniversalSearchQueryType,
} from 'services/casino';
import { InsightsItems } from 'config/Insights';
import { TimeFramesItems } from 'config/Timeframes';
import { isSlotCheckSearchEnabled } from 'lib/slotCheckSearchAccess';
import {
  trackUsGroupingChanged,
  trackUsMapTapped,
  trackUsResultTapped,
  trackUsSearchSubmitted,
  trackUsSuggestionTapped,
} from 'lib/universalSearchAnalytics';
import {
  getUniversalSearchCardsPerGroup,
  getUniversalSearchDrawerEmptyPercent,
  getUniversalSearchDrawerStartPercent,
  getUniversalSearchMapTopCasinos,
} from 'lib/universalSearchConfig';
import {
  UniversalGroupBy,
  UniversalMapView,
  clearSavedUniversalSearch,
  readSavedUniversalSearch,
  setUniversalSearchEntry,
  writeSavedUniversalSearch,
} from 'lib/universalSearchSession';
import {
  buildManufacturerGroups,
  gameDetailIds,
  pinsAsMapCasinos,
  resultsSummary,
  topCasinoWithLocation,
} from 'lib/universalSearchView';
import UniversalSearchMap from './UniversalSearchMap';
import UniversalSearchDrawer from './UniversalSearchDrawer';
import UniversalSearchCard from './UniversalSearchCard';

const SUGGEST_DEBOUNCE_MS = 250;
const MIN_QUERY_LEN = 2;

const insightShortName = (key: string): string =>
  InsightsItems.find((i) => String(i.value) === key)?.short_name || key;
const timeframeLabel = (key: string): string =>
  TimeFramesItems.find((t) => String(t.value) === key)?.label || key;

export const UniversalSearchPage = () => {
  const history = useHistory();
  const enabledFlag = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.slotCheckSearchEnabled
  );
  const enabled = isSlotCheckSearchEnabled(enabledFlag);
  const casinoSvc = useMemo(() => new CasinoService(), []);

  // Restore the session's saved search once (globe icon return).
  const savedRef = useRef(readSavedUniversalSearch());
  const saved = savedRef.current;

  const [input, setInput] = useState(saved?.query || '');
  const [focused, setFocused] = useState(false);
  const [suggest, setSuggest] = useState<IUniversalSearchSuggestResponse | null>(null);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [result, setResult] = useState<IUniversalSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [group, setGroup] = useState<UniversalGroupBy>(saved?.group || 'casino');
  const [expanded, setExpanded] = useState<Set<string>>(new Set(saved?.expanded || []));
  const [searchSeq, setSearchSeq] = useState(0);
  const [lastQuery, setLastQuery] = useState<{ q: string; type: UniversalSearchQueryType } | null>(
    saved ? { q: saved.query, type: saved.qType } : null
  );
  // Bottom of the Casino Selection header (130px toolbar + iOS safe-area inset):
  // the search bar floats on the map just below it; the drawer's open stop is there too.
  const [headerBottom, setHeaderBottom] = useState(130);
  const [searchBarBottom, setSearchBarBottom] = useState(190);
  const [snapSeq, setSnapSeq] = useState(0);
  // Every casino the search covers (map pins before a search) + its drawer config.
  const [pins, setPins] = useState<IUniversalSearchCasinoPin[]>([]);
  const [pinsConfig, setPinsConfig] = useState<{ drawerStartPercent?: number; drawerEmptyPercent?: number } | null>(null);

  const navRef = useRef<HTMLDivElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const contentElRef = useRef<HTMLDivElement | null>(null);
  const scrollTopRef = useRef<number>(saved?.scrollTop || 0);
  const pendingScrollRef = useRef<number | null>(saved ? saved.scrollTop || 0 : null);
  const drawerPercentRef = useRef<number | undefined>(saved?.drawerPercent);
  const mapViewRef = useRef<UniversalMapView | null>(saved?.map || null);
  const fullSubRef = useRef<Subscription | null>(null);
  // Latest grouping / expanded groups for saves made from async callbacks.
  const groupRef = useRef(group);
  groupRef.current = group;
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  const suggestSubRef = useRef<Subscription | null>(null);

  // No search yet (nothing typed + submitted, nothing restored): pins + low drawer.
  const isEmptyState = !result && !lastQuery;
  const searchStartPercent =
    result?.config?.drawerStartPercent ?? pinsConfig?.drawerStartPercent ?? getUniversalSearchDrawerStartPercent();
  const emptyPercent = pinsConfig?.drawerEmptyPercent ?? getUniversalSearchDrawerEmptyPercent();
  const startPercent = isEmptyState ? emptyPercent : searchStartPercent;
  const isEmptyStateRef = useRef(isEmptyState);
  isEmptyStateRef.current = isEmptyState;
  const cardsPerGroup = result?.config?.cardsPerGroup ?? getUniversalSearchCardsPerGroup();

  useEffect(() => {
    if (!enabled) history.replace('/auth/choosecasinos');
  }, [enabled, history]);

  useLayoutEffect(() => {
    const measure = () => {
      // The header (.app-header) is absolutely positioned, so measure it, not its wrapper.
      const header = navRef.current?.querySelector('.app-header') as HTMLElement | null;
      const bottom = header ? header.getBoundingClientRect().bottom : 0;
      if (bottom > 40) setHeaderBottom(Math.round(bottom));
      const bar = searchBarRef.current;
      if (bar) setSearchBarBottom(Math.round(bar.getBoundingClientRect().bottom));
    };
    measure();
    // Ionic sizes the toolbar (safe-area padding) after first paint.
    const t1 = window.setTimeout(measure, 50);
    const t2 = window.setTimeout(measure, 400);
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, []);

  // The search bar moves with the header height; keep its bottom (suggestions, map padding) current.
  useEffect(() => {
    const bar = searchBarRef.current;
    if (bar) setSearchBarBottom(Math.round(bar.getBoundingClientRect().bottom));
  }, [headerBottom, result, focused]);

  const persist = useCallback(
    (patch: Parameters<typeof writeSavedUniversalSearch>[0]) => writeSavedUniversalSearch(patch),
    []
  );

  const runSearch = useCallback(
    (q: string, type: UniversalSearchQueryType, keepView = false) => {
      const text = q.trim();
      if (text.length < MIN_QUERY_LEN) return;
      fullSubRef.current?.unsubscribe();
      suggestSubRef.current?.unsubscribe();
      setSuggest(null);
      setFocused(false);
      setLoading(true);
      setError(null);
      setLastQuery({ q: text, type });
      if (!keepView) {
        // A new search starts at the top of the list and re-centers the map.
        pendingScrollRef.current = 0;
        scrollTopRef.current = 0;
        mapViewRef.current = null;
        setExpanded(new Set());
        setSnapSeq((n) => n + 1);
      }
      fullSubRef.current = casinoSvc.GetUniversalSearch(text, type).subscribe({
        next: (resp) => {
          setLoading(false);
          if (!resp || resp.status !== 'ok') {
            setError("Slot Check Search couldn't load results. Try again.");
            return;
          }
          setResult(resp);
          setSearchSeq((n) => n + 1);
          if (!keepView) {
            trackUsSearchSubmitted({
              searchTerm: text,
              queryType: type,
              egms: resp.totals?.egms ?? 0,
              casinos: resp.totals?.casinos ?? 0,
              partial: !!resp.partial,
            });
          }
          persist({
            query: text,
            qType: type,
            group: groupRef.current,
            scrollTop: keepView ? scrollTopRef.current : 0,
            map: keepView ? mapViewRef.current : null,
            expanded: keepView ? Array.from(expandedRef.current) : [],
          });
        },
        error: (err: { response?: { status?: number } } | undefined) => {
          setLoading(false);
          const status = err?.response?.status;
          if (status === 403) {
            history.replace('/auth/choosecasinos');
            return;
          }
          setError("Slot Check Search couldn't load results. Try again.");
        },
      });
    },
    [casinoSvc, history, persist]
  );

  // First mount with a saved search: run it again (server-cached) and restore the view.
  useEffect(() => {
    if (enabled && saved) runSearch(saved.query, saved.qType, true);
    return () => {
      fullSubRef.current?.unsubscribe();
      suggestSubRef.current?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Map pins for the empty state: every casino the search covers (Mongo `casinos`).
  useEffect(() => {
    if (!enabled) return undefined;
    const sub = casinoSvc.GetUniversalSearchCasinos().subscribe({
      next: (resp) => {
        if (!resp || resp.status !== 'ok') return;
        setPins(resp.casinos || []);
        if (resp.config) {
          setPinsConfig(resp.config);
          // Server config differs from the local fallback: move the empty drawer to it.
          if (isEmptyStateRef.current && resp.config.drawerEmptyPercent !== getUniversalSearchDrawerEmptyPercent()) {
            setSnapSeq((n) => n + 1);
          }
        }
      },
      error: () => {
        // Pins are a nice-to-have: the search still works without them.
      },
    });
    return () => sub.unsubscribe();
  }, [enabled, casinoSvc]);

  const pinCasinos = useMemo(() => pinsAsMapCasinos(pins), [pins]);

  // Open with the keyboard up when there is no search to restore (best effort:
  // iOS may still need a tap before it shows the keyboard).
  useEffect(() => {
    if (!enabled || saved) return undefined;
    const t = window.setTimeout(() => inputRef.current?.focus(), 350);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Type-ahead suggestions (mode=suggest: names + counts, no metrics).
  useEffect(() => {
    if (!enabled || !focused) return;
    const text = input.trim();
    if (text.length < MIN_QUERY_LEN) {
      setSuggest(null);
      return;
    }
    const t = window.setTimeout(() => {
      suggestSubRef.current?.unsubscribe();
      setSuggestLoading(true);
      suggestSubRef.current = casinoSvc.GetUniversalSearchSuggest(text).subscribe({
        next: (resp) => {
          setSuggestLoading(false);
          setSuggest(resp && resp.status === 'ok' ? resp : null);
        },
        error: () => {
          setSuggestLoading(false);
          setSuggest(null);
        },
      });
    }, SUGGEST_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [input, focused, enabled, casinoSvc]);

  // Restore the drawer scroll once results for the restored search render.
  useEffect(() => {
    if (!result || pendingScrollRef.current === null) return;
    const target = pendingScrollRef.current;
    pendingScrollRef.current = null;
    window.requestAnimationFrame(() => {
      if (contentElRef.current) contentElRef.current.scrollTop = target;
    });
  }, [result, group]);

  // Leaving (into a casino, or via the menu): keep scroll + map for the globe icon.
  useIonViewWillLeave(() => {
    persist({ scrollTop: scrollTopRef.current, drawerPercent: drawerPercentRef.current, map: mapViewRef.current });
  });

  const manufacturerGroups = useMemo(() => buildManufacturerGroups(result), [result]);
  const centerCasino = useMemo(() => topCasinoWithLocation(result), [result]);
  const casinoById = useMemo(() => {
    const m = new Map<number, IUniversalSearchCasino>();
    (result?.casinos || []).forEach((c) => m.set(c.casinoId, c));
    return m;
  }, [result]);

  const changeGroup = (g: UniversalGroupBy) => {
    setGroup(g);
    scrollTopRef.current = 0;
    if (contentElRef.current) contentElRef.current.scrollTop = 0;
    persist({ group: g, scrollTop: 0 });
    trackUsGroupingChanged(g);
  };

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      persist({ expanded: Array.from(next) });
      return next;
    });
  };

  /** Enter the casino through the normal entry flow; Explore applies the game filter. */
  const enterCasino = (casino: IUniversalSearchCasino | undefined, gameName: string, openDetailId?: string) => {
    if (!casino) return;
    const ids = gameDetailIds(casino, gameName);
    trackUsResultTapped({
      resultType: openDetailId ? 'egm' : 'game',
      casinoId: casino.casinoId,
      gameName,
      grouping: group,
    });
    persist({
      scrollTop: scrollTopRef.current,
      drawerPercent: drawerPercentRef.current,
      map: mapViewRef.current,
      group,
      expanded: Array.from(expanded),
    });
    setUniversalSearchEntry({
      casinoId: casino.casinoId,
      filterName: gameName,
      detailIds: ids.length ? ids : openDetailId ? [openDetailId] : [],
      openDetailId: openDetailId ?? null,
    });
    history.push('/auth/choosecasinos?forceSelect=1&from=slotCheckSearch');
  };

  const onMapSelectCasino = (casinoId: number) => {
    if (group !== 'casino') setGroup('casino');
    window.requestAnimationFrame(() => {
      const el = contentElRef.current?.querySelector(`#us-casino-${casinoId}`) as HTMLElement | null;
      if (el && contentElRef.current) contentElRef.current.scrollTop = el.offsetTop - 8;
    });
  };

  const clearInput = () => {
    setInput('');
    setSuggest(null);
    inputRef.current?.focus();
  };

  /** Chip ✕: end this search (globe goes white) and start a new one. */
  const clearSearch = () => {
    fullSubRef.current?.unsubscribe();
    clearSavedUniversalSearch();
    setResult(null);
    setLastQuery(null);
    setError(null);
    setLoading(false);
    setInput('');
    setSuggest(null);
    setExpanded(new Set());
    setSnapSeq((n) => n + 1);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  /** Chip tap: edit the current query. */
  const editSearch = () => {
    setInput(lastQuery?.q || input);
    setFocused(true);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  if (!enabled) return null;

  const metricLabel = result
    ? `${insightShortName(result.config.cardMetric)} · ${timeframeLabel(result.config.cardTimeframe)}`
    : '';

  const drawerHeader = result ? (
    <div className="us-drawer__header">
      <div className="us-drawer__heading">Slot Check Search</div>
      <div className="us-drawer__title">
        {lastQuery?.q ? <span className="us-drawer__query">“{lastQuery.q}”</span> : null}
        <span className="us-drawer__summary">{resultsSummary(result.totals)}</span>
      </div>
      <div className="us-drawer__toolbar">
        <div className="us-toggle" role="tablist" aria-label="Group results">
          <button
            type="button"
            role="tab"
            aria-selected={group === 'casino'}
            className={`us-toggle__btn${group === 'casino' ? ' us-toggle__btn--active' : ''}`}
            onClick={() => changeGroup('casino')}
          >
            By Casino
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={group === 'manufacturer'}
            className={`us-toggle__btn${group === 'manufacturer' ? ' us-toggle__btn--active' : ''}`}
            onClick={() => changeGroup('manufacturer')}
          >
            By Manufacturer
          </button>
        </div>
      </div>
      <div className="us-drawer__listhead">
        <span>{group === 'casino' ? 'Casinos with the most matches first' : 'Manufacturers with the most matches first'}</span>
        <span className="us-drawer__metric">{metricLabel}</span>
      </div>
      {result.partial ? (
        <div className="us-notice">
          Some casinos didn&apos;t respond, so these results may be incomplete.{' '}
          <button type="button" className="us-link" onClick={() => lastQuery && runSearch(lastQuery.q, lastQuery.type, true)}>
            Retry
          </button>
        </div>
      ) : null}
      {result.capped ? (
        <div className="us-notice">Showing the top {result.config.maxEgms} EGMs. Counts include every match.</div>
      ) : null}
    </div>
  ) : (
    // No search yet: the drawer sits low (empty-state height) with the prompt; pins stay visible.
    <div className="us-drawer__header us-drawer__header--prompt">
      <div className="us-drawer__heading">Slot Check Search</div>
      <div className="us-drawer__subtitle">Search every active SlotCheck casino</div>
    </div>
  );

  const renderCasinoGroups = () =>
    (result?.casinos || []).map((c) => {
      const key = `c:${c.casinoId}`;
      const isOpen = expanded.has(key);
      const shown = isOpen ? c.egms : c.egms.slice(0, cardsPerGroup);
      return (
        <section className="us-group" id={`us-casino-${c.casinoId}`} key={key}>
          <div className="us-group__head">
            {c.logo ? <img className="us-group__logo" src={c.logo} alt="" /> : null}
            <div className="us-group__titles">
              <div className="us-group__title">{c.displayName}</div>
              <div className="us-group__sub">
                {[[c.city, c.state].filter(Boolean).join(', '), `${c.count} EGM${c.count === 1 ? '' : 's'}`, `${c.distinctGames} game${c.distinctGames === 1 ? '' : 's'}`]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
          </div>
          <div className="us-group__games">
            {c.games.slice(0, 8).map((g) => (
              <button type="button" key={g.name} className="us-chip" onClick={() => enterCasino(c, g.name)}>
                <span className="us-chip__name">{g.name}</span>
                <span className="us-chip__count">{g.count}</span>
              </button>
            ))}
          </div>
          {c.metricFailed ? <div className="us-group__note">Card values aren&apos;t available for this casino right now.</div> : null}
          {shown.map((e) => (
            <UniversalSearchCard
              key={e.detailId}
              egm={e}
              metricKey={c.metricKey}
              showMetric={c.metricAvailable !== false}
              onSelect={() => enterCasino(c, e.gameName || e.title, e.detailId)}
            />
          ))}
          {c.egms.length > cardsPerGroup ? (
            <button type="button" className="us-group__more" onClick={() => toggleExpanded(key)}>
              {isOpen ? 'Show less' : `Show all ${c.egms.length}`}
            </button>
          ) : null}
        </section>
      );
    });

  const renderManufacturerGroups = () =>
    manufacturerGroups.map((m) => {
      const isOpen = expanded.has(m.key);
      const shown = isOpen ? m.egms : m.egms.slice(0, cardsPerGroup);
      return (
        <section className="us-group" key={m.key}>
          <div className="us-group__head">
            <div className="us-group__titles">
              <div className="us-group__title">{m.name}</div>
              <div className="us-group__sub">
                {`${m.count} EGM${m.count === 1 ? '' : 's'} · ${m.casinos} casino${m.casinos === 1 ? '' : 's'} · ${m.games} game${m.games === 1 ? '' : 's'}`}
              </div>
            </div>
          </div>
          {shown.map((e) => (
            <UniversalSearchCard
              key={`${e.casinoId}:${e.detailId}`}
              egm={e}
              metricKey={e.metricKey}
              showMetric={e.showMetric}
              casinoName={e.casinoName}
              onSelect={() => enterCasino(casinoById.get(e.casinoId), e.gameName || e.title, e.detailId)}
            />
          ))}
          {m.egms.length > cardsPerGroup ? (
            <button type="button" className="us-group__more" onClick={() => toggleExpanded(m.key)}>
              {isOpen ? 'Show less' : `Show all ${m.egms.length}`}
            </button>
          ) : null}
        </section>
      );
    });

  const showSuggest = focused && input.trim().length >= MIN_QUERY_LEN;
  // Chip (mock 03) once a search ran and the user is not editing; input otherwise.
  const showChip = !!result && !!lastQuery && !focused;

  return (
    <IonPage className="us-page">
      <IonContent scrollY={false} fullscreen className="us-page__content">
        <div className="us-page__map">
          <UniversalSearchMap
            casinos={isEmptyState ? pinCasinos : result?.casinos || []}
            overview={isEmptyState}
            centerCasinoId={centerCasino ? centerCasino.casinoId : null}
            topCasinos={getUniversalSearchMapTopCasinos()}
            initialView={saved?.map || null}
            searchSeq={searchSeq}
            topPadding={searchBarBottom}
            bottomPadding={Math.round((typeof window !== 'undefined' ? window.innerHeight : 800) * (startPercent / 100))}
            onSelectCasino={onMapSelectCasino}
            onViewChange={(v) => {
              mapViewRef.current = v;
            }}
            onPinTap={(casinoId) => trackUsMapTapped({ target: 'pin', casinoId })}
            onClusterTap={(size) => trackUsMapTapped({ target: 'cluster', clusterSize: size })}
          />
        </div>

        {/* Casino Selection header (absolute, 130px + safe area) over the map. */}
        <div className="casino-fixed-top__nav us-page__nav" ref={navRef}>
          <CasinoNavBar hideSearch />
        </div>

        {/* Search bar floats on the map just below the header (above the map, below the drawer). */}
        <div className="us-searchbar" ref={searchBarRef} style={{ top: `${headerBottom + 8}px` }}>
          {showChip ? (
            <div className="us-chip-search">
              <button type="button" className="us-chip-search__label" onClick={editSearch} aria-label="Edit search">
                <IonIcon icon={search} />
                <span>{lastQuery?.q}</span>
              </button>
              <button type="button" className="us-chip-search__clear" onClick={clearSearch} aria-label="Clear search">
                <IonIcon icon={closeCircle} />
              </button>
            </div>
          ) : (
            <form
              className="us-searchbar__form"
              onSubmit={(e) => {
                e.preventDefault();
                runSearch(input, 'text');
              }}
            >
              <TextField
                id="slot-check-search-input"
                label="Search"
                placeholder="Game, manufacturer or asset #"
                variant="outlined"
                fullWidth
                value={input}
                inputRef={inputRef}
                onFocus={() => setFocused(true)}
                onBlur={() => window.setTimeout(() => setFocused(false), 150)}
                onChange={(e) => {
                  setInput(e.target.value);
                  setFocused(true);
                }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IonIcon icon={search} className="search-box-section-icon" />
                    </InputAdornment>
                  ),
                  endAdornment: input ? (
                    <InputAdornment position="end" onClick={clearInput} style={{ cursor: 'pointer' }}>
                      <IonIcon icon={closeCircle} className="search-box-section-icon" />
                    </InputAdornment>
                  ) : undefined,
                }}
                inputProps={{ autoComplete: 'off', enterKeyHint: 'search', maxLength: 64 }}
              />
            </form>
          )}
        </div>

        {showSuggest ? (
          <div className="us-suggest" style={{ top: `${searchBarBottom + 4}px` }}>
            {suggestLoading && !suggest ? <div className="us-suggest__row us-suggest__row--muted">Searching…</div> : null}
            {suggest && !suggest.games.length && !suggest.manufacturers.length && !suggest.assets.length ? (
              <div className="us-suggest__row us-suggest__row--muted">No matches at any casino</div>
            ) : null}
            {suggest && suggest.games.length ? <div className="us-suggest__section">Games</div> : null}
            {suggest?.games.map((g) => (
              <button
                type="button"
                key={`g:${g.name}`}
                className="us-suggest__row"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setInput(g.name);
                  trackUsSuggestionTapped({ suggestionType: 'game', searchTerm: input.trim() });
                  runSearch(g.name, 'game');
                }}
              >
                <span className="us-suggest__name">{g.name}</span>
                <span className="us-suggest__count">
                  {g.egms} EGM{g.egms === 1 ? '' : 's'} · {g.casinos} casino{g.casinos === 1 ? '' : 's'}
                </span>
              </button>
            ))}
            {suggest && suggest.manufacturers.length ? <div className="us-suggest__section">Manufacturers</div> : null}
            {suggest?.manufacturers.map((m) => (
              <button
                type="button"
                key={`m:${m.name}`}
                className="us-suggest__row"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setInput(m.name);
                  trackUsSuggestionTapped({ suggestionType: 'manufacturer', searchTerm: input.trim() });
                  runSearch(m.name, 'manufacturer');
                }}
              >
                <span className="us-suggest__name">{m.name}</span>
                <span className="us-suggest__count">
                  {m.egms} EGM{m.egms === 1 ? '' : 's'} · {m.casinos} casino{m.casinos === 1 ? '' : 's'}
                </span>
              </button>
            ))}
            {suggest && suggest.assets.length ? <div className="us-suggest__section">Assets</div> : null}
            {suggest?.assets.map((a) => (
              <button
                type="button"
                key={`a:${a.casinoId}:${a.detailId}`}
                className="us-suggest__row"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setInput(a.displayId);
                  trackUsSuggestionTapped({ suggestionType: 'asset', searchTerm: input.trim() });
                  runSearch(a.displayId, 'text');
                }}
              >
                <span className="us-suggest__name">#{a.displayId} {a.gameName}</span>
                <span className="us-suggest__count">{a.casinoName}</span>
              </button>
            ))}
            {suggest ? (
              <button
                type="button"
                className="us-suggest__row us-suggest__row--all"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  trackUsSuggestionTapped({ suggestionType: 'all', searchTerm: input.trim() });
                  runSearch(input, 'text');
                }}
              >
              See all results for “{input.trim()}”
              </button>
            ) : null}
          </div>
        ) : null}

        <UniversalSearchDrawer
          header={drawerHeader}
          startPercent={startPercent}
          initialPercent={drawerPercentRef.current}
          topOffset={headerBottom}
          snapToStartSeq={snapSeq}
          onPercentChange={(p) => {
            drawerPercentRef.current = p;
            if (result) persist({ drawerPercent: p });
          }}
          onScroll={(top) => {
            scrollTopRef.current = top;
          }}
          contentRef={(el) => {
            contentElRef.current = el;
          }}
        >
          {loading ? (
            <div className="us-loading us-loading--inline">
              <IonSpinner name="crescent" />
            </div>
          ) : null}
          {!result && !loading ? (
            <div className="us-empty us-empty--prompt">
              {error ? (
                <>
                  {error}{' '}
                  {lastQuery ? (
                    <button type="button" className="us-link" onClick={() => runSearch(lastQuery.q, lastQuery.type, true)}>
                      Retry
                    </button>
                  ) : null}
                </>
              ) : (
                'Search every SlotCheck casino at once: a game, a manufacturer, or an asset number.'
              )}
            </div>
          ) : null}
          {result && result.casinos.length === 0 ? (
            <div className="us-empty">No EGMs match “{lastQuery?.q}” at any casino.</div>
          ) : result && group === 'casino' ? (
            renderCasinoGroups()
          ) : result ? (
            renderManufacturerGroups()
          ) : null}
        </UniversalSearchDrawer>
      </IonContent>
    </IonPage>
  );
};

export default UniversalSearchPage;
